import { Box, Button, Dialog, Typography, keyframes } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useColorMode } from "src/design/ColorModeProvider";
import { heroLine, heroPanel, radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import { HeroDecoration } from "src/grading/components/HeroBand/HeroBand";

export interface RevealOverlayProps {
  open: boolean;
  className: string;
  benchmarkVersion: string | number;
  counts: { presidential: number; national: number; participation: number };
  onPrint: () => void;
  onClose: () => void;
}

/** [left %, top %, size px] */
const STARS: ReadonlyArray<readonly [number, number, number]> = [
  [8, 18, 22],
  [88, 12, 30],
  [16, 78, 18],
  [80, 72, 24],
  [50, 6, 16],
  [94, 44, 14],
  [4, 48, 16],
  [64, 88, 20],
];

const TIERS = ["presidential", "national", "participation"] as const;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.92); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

/**
 * Celebration card shown after a session is finalized (design `reveal.show`): a hero-blue card
 * with diagonal bands, dots and stars, the award counts per tier and the print action.
 */
export const RevealOverlay = ({
  open,
  className,
  benchmarkVersion,
  counts,
  onPrint,
  onClose,
}: RevealOverlayProps) => {
  const { t } = useTranslation();
  const { mode, reducedMotion } = useColorMode();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const total = counts.presidential + counts.national + counts.participation;

  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => headingRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  const labels = {
    presidential: t("awards.presidential", { defaultValue: "Presidential" }),
    national: t("awards.national", { defaultValue: "National" }),
    participation: t("awards.participation", { defaultValue: "Participation" }),
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="reveal-title"
      maxWidth={false}
      PaperProps={{
        sx: (theme) => ({
          position: "relative",
          width: "min(820px, 100%)",
          m: 2.5,
          p: 0,
          borderRadius: "28px",
          overflow: "hidden",
          bgcolor: theme.palette.hero,
          color: theme.palette.heroInk,
          backgroundImage: "none",
        }),
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(6px)" } } }}
    >
      <HeroDecoration
        bandWidth="40%"
        clip="polygon(40% 0, 100% 0, 100% 100%, 0 100%)"
      />
      {STARS.map(([left, top, size], i) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          component="span"
          aria-hidden="true"
          data-star
          data-animated={reducedMotion ? "false" : "true"}
          sx={(theme) => ({
            position: "absolute",
            left: `${left}%`,
            top: `${top}%`,
            fontSize: size,
            lineHeight: 1,
            color: theme.palette.heroInk,
            textShadow: `0 0 18px ${theme.palette.heroInk}99`,
            animation: reducedMotion
              ? "none"
              : `${popIn} 700ms cubic-bezier(.2,1.6,.4,1) ${i * 60}ms both`,
          })}
        >
          ★
        </Box>
      ))}
      <Box
        sx={{
          position: "relative",
          p: { xs: 3, md: "36px 40px" },
          display: "flex",
          flexDirection: "column",
          gap: 3.25,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            maxWidth: 520,
          }}
        >
          <Box
            component="span"
            sx={(theme) => ({
              alignSelf: "flex-start",
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1,
              fontStretch: fontStretch.display,
              textTransform: "uppercase",
              px: "11px",
              py: "7px",
              borderRadius: `${radius.pill}px`,
              bgcolor: theme.palette.heroInk,
              color: theme.palette.hero,
            })}
          >
            {t("review.revealEyebrow", {
              version: benchmarkVersion,
              defaultValue: "Finalized · Benchmarks v{{version}}",
            })}
          </Box>
          <Typography
            id="reveal-title"
            variant="h1"
            component="h1"
            ref={headingRef}
            tabIndex={-1}
            sx={{
              m: 0,
              fontSize: { xs: 32, md: 44 },
              lineHeight: 0.95,
              textTransform: "uppercase",
              outline: "none",
            }}
          >
            {t("review.revealTitle", {
              className,
              total,
              defaultValue: "{{className}} earned {{total}} awards",
            })}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 1.75,
            maxWidth: 600,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          {TIERS.map((tier, i) => (
            <Box
              key={tier}
              data-tier={tier}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1.25,
                p: 2.25,
                borderRadius: `${radius.card}px`,
                bgcolor: heroPanel[mode],
                border: `1.5px solid ${heroLine[mode]}`,
                animation: reducedMotion
                  ? "none"
                  : `${popIn} 550ms cubic-bezier(.2,1.4,.4,1) ${200 + i * 120}ms both`,
              }}
            >
              <Box
                component="img"
                src={`/assets/tiers/${tier}.svg`}
                alt=""
                aria-hidden="true"
                sx={{ width: 52, height: 52 }}
              />
              <Typography
                variant="stat"
                component="div"
                sx={{ fontSize: 52, color: "heroInk" }}
              >
                {counts[tier]}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  fontStretch: fontStretch.display,
                  textTransform: "uppercase",
                  color: "heroInk",
                }}
              >
                {labels[tier]}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={onPrint}
            sx={(theme) => ({
              bgcolor: theme.palette.heroInk,
              color: theme.palette.hero,
              fontSize: 15,
              minHeight: 52,
              px: 3.25,
              "&:hover": { bgcolor: theme.palette.heroInk },
            })}
          >
            {t("review.printCertificates", {
              defaultValue: "Print certificates & letters →",
            })}
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={(theme) => ({
              borderWidth: 2,
              borderColor: `${theme.palette.heroInk}99`,
              color: theme.palette.heroInk,
              minHeight: 48,
              px: 2.5,
              "&:hover": {
                borderWidth: 2,
                borderColor: theme.palette.heroInk,
                bgcolor: "transparent",
              },
            })}
          >
            {t("review.backToReview", { defaultValue: "Back to review" })}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default RevealOverlay;
